import { Link } from "@/components/link";
import { PageCard, PageCardHeader, PageCardTitle } from "@/components/PageCard";
import PageHeader from "@/components/PageHeader";
import { CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Module, PATH_ROOT, PATH_TEMPLATES } from "@/constants";
import usePaginatedList from "@/hooks/usePaginatedList";
import { fetchSurveyTemplateList } from "@/services/survey-templates";
import { ReadSurveyTemplate } from "@/types/survey-template";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CirclePlus, NotepadTextDashed } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const SurveyTemplateList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const queryTemplates = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }) => {
    return await fetchSurveyTemplateList({
      lastItem: pageParam,
      filters: [],
      sorting: undefined,
    });
  };

  const query = useInfiniteQuery({
    queryKey: [Module.SurveyTemplates],
    initialPageParam: undefined,
    queryFn: queryTemplates,
    getNextPageParam: (lastPage) => {
      return lastPage.last_item;
    },
  });

  const editTemplate = (templateId: string | undefined) => {
    navigate(`${PATH_TEMPLATES}/${templateId}`);
  };

  const templates = usePaginatedList<ReadSurveyTemplate>(query);

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveyTemplates") },
    ],
    [t],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <PageCard>
        <PageCardHeader>
          <PageCardTitle>
            <NotepadTextDashed className="mr-2" />{" "}
            {t("surveyTemplateList.title")}
          </PageCardTitle>
          <Link to={`${PATH_TEMPLATES}/new`}>
            <CirclePlus className="w-5 h-5 mr-3" />
            {t("surveyTemplateList.addSurveyTemplate")}
          </Link>
        </PageCardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">
                  {t("surveyTemplateList.surveyTemplateName")}
                </TableHead>
                <TableHead>
                  {t("surveyTemplateList.surveyTemplateDescription")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(({ id, name, description }) => (
                <TableRow
                  key={id}
                  className="cursor-pointer"
                  onClick={() => editTemplate(id)}
                >
                  <TableCell className="font-bold">{name}</TableCell>
                  <TableCell>{description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </PageCard>
    </>
  );
};

export default SurveyTemplateList;
